'use client';

import { c as _c } from "react/compiler-runtime";
import './panel-people.scss';
import { nanoid } from 'nanoid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Form } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PhoneField } from '@sola/ark-core/molecules/phone-field/phone-field.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { useAuth } from '@sola/auth-sdk/store.tsx';
import type { VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import { trackVolunteerGuest } from '@sola/tracking/lib/analytics/events-volunteer.ts';
import { SelectableCard } from '../../../molecules/selectable-card/selectable-card.tsx';
import { ShareButtons, ShareButtonsTrigger } from '../../../molecules/share-buttons/share-buttons.tsx';
import type { GuestAgeRange, GuestConflict, GuestRelationship } from '../../../utils/volunteer-utils.ts';
import { buildShareSummary, getContactDisplayName, isMinor } from '../../../utils/volunteer-utils.ts';
import type { useVolunteerRegistrationSheet } from '../volunteer-registration-sheet.logic.ts';
type Logic = ReturnType<typeof useVolunteerRegistrationSheet>;
export const PanelPeople: React.FC<{
  logic: Logic;
  location?: VolunteerLocationItemDto;
}> = t0 => {
  const $ = _c(91);
  const {
    logic,
    location
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const shareTitle = location?.locationName;
  let t2;
  if ($[1] !== location || $[2] !== t) {
    t2 = location ? buildShareSummary(t, {
      location
    }) : undefined;
    $[1] = location;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shareSummary = t2;
  const [guestAgeRange, setGuestAgeRange] = useState("");
  const [guestRelationship, setGuestRelationship] = useState("");
  const [guestError, setGuestError] = useState(null);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["nonresident-child", "friend", "relative", "other"];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const nonHouseholdRelationships = t3;
  const isNonHouseholdGuest = guestRelationship !== "" && nonHouseholdRelationships.includes(guestRelationship);
  const {
    actions: authActions
  } = useAuth("actions");
  const [, setMailtoHref] = useState("mailto:");
  const atMax = logic.selectedContactIds.length >= logic.totalAvailableSlots && logic.totalAvailableSlots > 0;
  const exceedsAvailableSlots = atMax && logic.contacts.length > logic.totalAvailableSlots;
  const hasLoadError = logic.contactsError || logic.slotsError;
  const isLoadingPeople = !hasLoadError && (logic.contactsPending || logic.slotsPending);
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const currentUrl = globalThis.location.href;
      const body = encodeURIComponent(`${currentUrl}`);
      setMailtoHref(`mailto:?body=${body}`);
    };
    t5 = [];
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== t) {
    t6 = t("volunteers:registration.peopleTitle");
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <h3 className="heading-xs md:heading-sm mb-snug-lg">{t6}</h3>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t) {
    t8 = t("volunteers:registration.peopleDescription");
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t8) {
    t9 = <p className="body-sm body-neutral-mild pb-comfy-xs">{t8}</p>;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t9) {
    t10 = <div className="flex-1">{t7}{t9}</div>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== logic.supporterName) {
    t11 = {
      person: logic.supporterName
    };
    $[18] = logic.supporterName;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== authActions) {
    t12 = () => authActions.logOut();
    $[20] = authActions;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t) {
    t13 = t("volunteers:registration.peopleLoginSomeoneElse");
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = {
      1: <LinkStyle noPadding={true} noUnderline={true} size="inherit"><button type="button" onClick={t12}>{t13}</button></LinkStyle>
    };
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t11 || $[28] !== t14) {
    t15 = <div className="PanelPeople__login body-md body-neutral-mild pb-comfy-lg "><IntlBlock i18nKey="volunteers:registration.peopleLoggedText" values={t11} components={t14} /></div>;
    $[27] = t11;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== hasLoadError || $[31] !== t) {
    t16 = hasLoadError && <div className="pb-snug-lg"><Alert variant="warning" title={t("volunteers:registration.peopleLoadErrorTitle")} message={t("volunteers:registration.peopleLoadErrorBody")} /></div>;
    $[30] = hasLoadError;
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== isLoadingPeople) {
    t17 = isLoadingPeople && <ul className="PanelPeople__list flex flex-col gap-snug-lg p-none m-none" aria-hidden="true">{Array.from({
        length: 3
      }, _temp)}</ul>;
    $[33] = isLoadingPeople;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== hasLoadError || $[36] !== isLoadingPeople || $[37] !== logic.contacts.length || $[38] !== t) {
    t18 = !hasLoadError && !isLoadingPeople && logic.contacts.length === 0 && <div className="pb-snug-lg"><Alert variant="warning" title={t("volunteers:registration.noIndividualsAlertTitle")} message={t("volunteers:registration.noIndividualsAlertBody")} /></div>;
    $[35] = hasLoadError;
    $[36] = isLoadingPeople;
    $[37] = logic.contacts.length;
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = `PanelPeople__list flex flex-col gap-snug-lg p-none m-none ${isLoadingPeople || hasLoadError ? "hidden" : ""}`;
  let t20;
  if ($[40] !== t) {
    t20 = t("volunteers:registration.peopleTitle");
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== logic || $[43] !== t) {
    t21 = logic.contacts.map(contact => {
      const isSelected = logic.selectedContactIds.includes(contact.id);
      const contactAtMax = !isSelected && logic.selectedContactIds.length >= logic.totalAvailableSlots;
      const fullName = getContactDisplayName(contact);
      const initials = getContactDisplayName(contact, {
        variant: "initials"
      });
      const isPreviouslyRegistered = logic.previouslyRegisteredContactIds.has(contact.id);
      return <li key={contact.id}><SelectableCard checked={isSelected} disabled={contactAtMax} onChange={() => logic.toggleContact(contact.id)} checkIcon={<ArkIcon iconName="check" color="currentColor" size="20" />}><span className={`PanelPeople__initials flex items-center justify-center rounded-full heading-sm text-center pt-comfy-xs px-comfy-xs shrink-0 ${isSelected ? "PanelPeople__initials--selected" : ""}`}>{initials}</span><span className="flex flex-col gap-snug-md flex-1 min-w-0"><span className={`label-potent label-lg PanelPeople__name ${isSelected ? "PanelPeople__name--selected" : ""}`}>{fullName}</span>{contact.email && <span className="label-lg label-mild label-neutral-moderate PanelPeople__email">{contact.email}</span>}{isPreviouslyRegistered && <span className="label-md label-mild label-neutral-mild">{t("volunteers:registration.alreadyRegistered")}</span>}</span></SelectableCard></li>;
    });
    $[42] = logic;
    $[43] = t;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t19 || $[46] !== t20 || $[47] !== t21) {
    t22 = <ul className={t19} role="group" aria-label={t20}>{t21}</ul>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== atMax || $[50] !== hasLoadError || $[51] !== isLoadingPeople || $[52] !== logic || $[53] !== t) {
    t23 = !isLoadingPeople && !hasLoadError && !logic.isAddingGuest && !atMax && <button type="button" onClick={() => logic.setIsAddingGuest(true)} className="PanelPeople__add-guest flex flex-col justify-center items-center w-full max-w-sm p-comfy-sm gap-snug-md rounded-md cursor-pointer my-snug-xl"><span className="flex items-center gap-snug-md label-potent label-lg"><ArkIcon iconName="plus" width="24" height="24" /><span>{t("volunteers:registration.addFamilyMember")}</span></span><span className="label-mild label-sm label-neutral-mild"><em>{t("volunteers:registration.mustBeHousehold")}</em></span></button>;
    $[49] = atMax;
    $[50] = hasLoadError;
    $[51] = isLoadingPeople;
    $[52] = logic;
    $[53] = t;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== exceedsAvailableSlots || $[56] !== logic.totalAvailableSlots || $[57] !== t) {
    t24 = exceedsAvailableSlots && <div className="pt-snug-lg"><Alert message={t("volunteers:registration.maxPeopleReached", {
        count: logic.totalAvailableSlots
      })} /></div>;
    $[55] = exceedsAvailableSlots;
    $[56] = logic.totalAvailableSlots;
    $[57] = t;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== logic.hasAdultSelected || $[60] !== logic.selectedContactIds.length || $[61] !== t) {
    t25 = logic.selectedContactIds.length > 0 && !logic.hasAdultSelected && <div className="pt-snug-lg"><Alert message={t("volunteers:registration.minorNeedsAdult")} /></div>;
    $[59] = logic.hasAdultSelected;
    $[60] = logic.selectedContactIds.length;
    $[61] = t;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== guestAgeRange || $[64] !== guestError || $[65] !== isNonHouseholdGuest || $[66] !== location || $[67] !== logic || $[68] !== shareSummary || $[69] !== shareTitle || $[70] !== t) {
    t26 = logic.isAddingGuest && <Form onSubmit={data => {
      const ageRange = data.guestAgeRange as GuestAgeRange;
      const relationship = isMinor(ageRange) ? "child" : data.guestRelationship as GuestRelationship;
      const conflict = logic.addGuest({
        id: nanoid(),
        firstName: (data.guestFirstName as string).trim(),
        lastName: (data.guestLastName as string).trim(),
        email: (data.guestEmail as string).trim(),
        phone: (data.guestPhone as string)?.trim() || null,
        ageRange,
        relationship
      });
      if (conflict) {
        setGuestError(conflict);
        return;
      }
      if (location) {
        trackVolunteerGuest({
          guestRelationship: relationship,
          guestAge: ageRange,
          location,
          isTour: (location.tourCount ?? 0) > 1
        });
      }
      setGuestError(null);
      setGuestAgeRange("");
      setGuestRelationship("");
    }} className="PanelPeople__guest-form gap-comfy-md py-comfy-md rounded border border-neutral-300 bg-neutral-50" aria-label={t("volunteers:registration.addFamilyMember")}><h4 className="PanelPeople__guest-form-title heading-xs heading-neutral-amplified pb-comfy-xs">{t("volunteers:registration.familyMemberDetails")}</h4><div className="flex gap-comfy-sm"><div className="flex-1"><InputField label={t("volunteers:registration.guestFirstName")} name="guestFirstName" required={true} /></div><div className="flex-1"><InputField label={t("volunteers:registration.guestLastName")} name="guestLastName" required={true} /></div></div><div className="flex gap-comfy-sm"><div className="flex-1"><Select label={t("volunteers:registration.guestAgeRange")} name="guestAgeRange" items={[{
            value: "14-15"
          }, {
            value: "16-17"
          }, {
            value: "18+"
          }]} placeholder={t("volunteers:registration.guestAgeRange")} required={true} onChange={e => setGuestAgeRange(e.target.value as GuestAgeRange | '')} /></div><div className="flex-1"><Select label={t("volunteers:registration.guestRelationship")} name="guestRelationship" items={[{
            text: t("volunteers:registration.relationshipChild"),
            value: "child"
          }, {
            text: t("volunteers:registration.relationshipNonresidentChild"),
            value: "nonresident-child"
          }, {
            text: t("volunteers:registration.relationshipSpouseSelf"),
            value: "spouse-self"
          }, {
            text: t("volunteers:registration.relationshipFriend"),
            value: "friend"
          }, {
            text: t("volunteers:registration.relationshipRelative"),
            value: "relative"
          }, {
            text: t("volunteers:registration.relationshipOther"),
            value: "other"
          }]} placeholder={t("volunteers:registration.guestRelationship")} required={true} onChange={e_0 => setGuestRelationship(e_0.target.value as GuestRelationship | '')} /></div></div><EmailField label={guestAgeRange && isMinor(guestAgeRange) ? t("volunteers:registration.guestParentEmail") : t("volunteers:registration.guestEmail")} name="guestEmail" required={true} /><PhoneField label={t("volunteers:registration.guestPhone")} name="guestPhone" countryCode="US" />{logic.supporterAddress && <div className="PanelPeople__address-card p-comfy-sm body-sm rounded-md"><span className="label label-potent label-lg label-neutral-mild block mb-snug-sm">{t("volunteers:registration.mailingAddressLabel")}</span><span className="block">{[logic.supporterAddress.street, logic.supporterAddress.street2].filter(Boolean).join(", ")}</span><span className="block">{logic.supporterAddress.city}, {logic.supporterAddress.state}{" "}{logic.supporterAddress.postalCode}</span></div>}{!isNonHouseholdGuest && <Checkbox label={t("volunteers:registration.guestFamilyConfirm")} name="guestFamilyConfirm" required={true} />}{isNonHouseholdGuest && <Alert variant="warning" title={t("volunteers:registration.nonHouseholdAlertTitle")} message={t("volunteers:registration.nonHouseholdAlertBody")} action={<ShareButtons shareTitle={shareTitle} shareSummary={shareSummary}><ShareButtonsTrigger><LinkStyle noPadding={true} noUnderline={true} iconAfter="share-02"><button type="button">{t("volunteers:registration.shareThisEvent")}</button></LinkStyle></ShareButtonsTrigger></ShareButtons>} />}{guestError && <Alert variant="negative" message={t(guestError === "duplicate" ? "volunteers:registration.guestDuplicateError" : "volunteers:registration.guestEmailInUseError")} />}<div className="flex gap-comfy-sm pt-comfy-xs"><ButtonStyle variant="secondary" size="lg" className="flex-1"><button type="button" onClick={() => {
            setGuestAgeRange("");
            setGuestRelationship("");
            setGuestError(null);
            logic.setIsAddingGuest(false);
          }}>{t("volunteers:registration.guestCancel")}</button></ButtonStyle><ButtonStyle variant="primary" size="lg" className="flex-1"><button type="submit" disabled={isNonHouseholdGuest}>{t("volunteers:registration.guestAddVolunteer")}</button></ButtonStyle></div></Form>;
    $[63] = guestAgeRange;
    $[64] = guestError;
    $[65] = isNonHouseholdGuest;
    $[66] = location;
    $[67] = logic;
    $[68] = shareSummary;
    $[69] = shareTitle;
    $[70] = t;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== logic.isAddingGuest || $[73] !== shareSummary || $[74] !== shareTitle || $[75] !== t) {
    t27 = !logic.isAddingGuest && <div className="label-md label-moderate label-neutral-subtle my-comfy-lg"><IntlBlock i18nKey="volunteers:registration.peopleShareText" components={{
        1: <ShareButtons as="span" shareTitle={shareTitle} shareSummary={shareSummary}><ShareButtonsTrigger><LinkStyle noPadding={true}><button type="button">{t("volunteers:registration.shareThisEvent")}</button></LinkStyle></ShareButtonsTrigger></ShareButtons>
      }} /></div>;
    $[72] = logic.isAddingGuest;
    $[73] = shareSummary;
    $[74] = shareTitle;
    $[75] = t;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== t15 || $[78] !== t16 || $[79] !== t17 || $[80] !== t18 || $[81] !== t22 || $[82] !== t23 || $[83] !== t24 || $[84] !== t25 || $[85] !== t26 || $[86] !== t27) {
    t28 = <div className="py-comfy-lg md:py-roomy-xs max-w-sm mx-auto w-full">{t15}{t16}{t17}{t18}{t22}{t23}{t24}{t25}{t26}{t27}</div>;
    $[77] = t15;
    $[78] = t16;
    $[79] = t17;
    $[80] = t18;
    $[81] = t22;
    $[82] = t23;
    $[83] = t24;
    $[84] = t25;
    $[85] = t26;
    $[86] = t27;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== t10 || $[89] !== t28) {
    t29 = <div className="flex flex-col max-w-md mx-auto">{t10}{t28}</div>;
    $[88] = t10;
    $[89] = t28;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  return t29;
};
function _temp(_, index) {
  return <li key={index}><Skeleton height={72} borderRadius={8} /></li>;
}